import { useState, useEffect } from "react";
import { X, Download, Smartphone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const BANNER_DISMISSED_KEY = "pwa-install-banner-dismissed";
const DISMISS_DURATION_DAYS = 7;

// Export the hook for use in other components
export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    setIsInstalled(isStandalone);

    // Listen for install prompt (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return false;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      return true;
    }
    return false;
  };

  return { deferredPrompt, isInstalled, handleInstall };
};

// Standalone Install Button component for use anywhere
export const InstallButton = ({ 
  variant = "default",
  size = "default",
  className = ""
}: { 
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}) => {
  const { deferredPrompt, isInstalled, handleInstall } = useInstallPrompt();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobile = /iphone|ipad|ipod|android|webos|blackberry|windows phone/.test(userAgent);
    setIsMobile(mobile);
  }, []);

  // Don't show if already installed
  if (isInstalled) return null;

  // If we have a deferred prompt (Android/Chrome), show direct install button
  if (deferredPrompt) {
    return (
      <Button 
        variant={variant}
        size={size}
        onClick={handleInstall}
        className={`gap-2 ${className}`}
      >
        <Download className="w-4 h-4" />
        Install CheckFirst
      </Button>
    );
  }

  // For iOS or when no prompt available, link to install page
  if (isMobile) {
    return (
      <Button 
        variant={variant}
        size={size}
        asChild
        className={`gap-2 ${className}`}
      >
        <a href="/install">
          <Plus className="w-4 h-4" />
          Add to Home Screen
        </a>
      </Button>
    );
  }

  return null;
};

export const InstallBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { deferredPrompt, isInstalled, handleInstall } = useInstallPrompt();

  useEffect(() => {
    // Check if mobile device
    const userAgent = navigator.userAgent.toLowerCase();
    const mobile = /iphone|ipad|ipod|android|webos|blackberry|windows phone/.test(userAgent);
    setIsMobile(mobile);

    // Check if already installed (standalone mode)
    if (isInstalled) return;

    // Check if banner was recently dismissed
    const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedDate = new Date(parseInt(dismissedAt));
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < DISMISS_DURATION_DAYS) return;
    }

    // Show banner for mobile users after a short delay
    if (mobile) {
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isInstalled]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
  };

  const onInstall = async () => {
    const installed = await handleInstall();
    if (installed) {
      setIsVisible(false);
    }
  };

  if (!isVisible || !isMobile || isInstalled) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
      <div className="bg-card border border-border rounded-xl shadow-lg p-4 max-w-md mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-primary/10 flex items-center justify-center">
            <img 
              src="/pwa-icon-192.png?v=2" 
              alt="CheckFirst"
              className="w-10 h-10 object-contain"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-sm">
              Install CheckFirst
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add to your home screen for quick access anytime
            </p>
            
            <div className="flex items-center gap-2 mt-3">
              {deferredPrompt ? (
                <Button size="sm" onClick={onInstall} className="gap-1.5 h-9 text-xs font-medium">
                  <Download className="w-4 h-4" />
                  Install Now
                </Button>
              ) : (
                <Button 
                  size="sm" 
                  asChild
                  className="gap-1.5 h-9 text-xs font-medium"
                >
                  <a href="/install">
                    <Plus className="w-4 h-4" />
                    Add to Home Screen
                  </a>
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleDismiss}
                className="h-9 text-xs text-muted-foreground"
              >
                Not now
              </Button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Dismiss install banner"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
};
